"use client";

import React from "react";
import { ShieldCheck, Info, Globe, Anchor, Plane, Truck, Building2, PackageCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
const partners = [{
  id: 1,
  name: "EuroFreight Express",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/eurofreight_logo.png",
  type: "Air Freight Specialist",
  description: "Rapid transit solutions connecting major European hubs with Indian metropolitan airports.",
  certificates: "ISO 9001:2015, AEO Certified, IATA Accredited",
  icon: "plane"
}, {
  id: 2,
  name: "IndoOceanic Shipping",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/indooceanic_logo.png",
  type: "Maritime Logistics",
  description: "High-volume container shipping routes between Rotterdam, Antwerp, and Mumbai ports.",
  certificates: "ISO 14001, FMC Licensed, FIATA Member",
  icon: "anchor"
}, {
  id: 3,
  name: "TransContinental Customs",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/transcontinental_logo.png",
  type: "Customs Brokerage",
  description: "Expert navigation of EU-India Free Trade Agreement regulations and compliance.",
  certificates: "AEO-F Status, Licensed Customs Broker",
  icon: "globe"
}, {
  id: 4,
  name: "ColdChain Solutions EU",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/coldchain_logo.png",
  type: "Temperature Controlled",
  description: "End-to-end pharmaceutical and perishable goods transport across continents.",
  certificates: "GDP Certified, ISO 22000",
  icon: "truck"
}, {
  id: 5,
  name: "Bharat Rail & Road",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/bharat_rail_logo.png",
  type: "Inland Distribution",
  description: "Extensive last-mile delivery network covering all major Indian industrial zones.",
  certificates: "ISO 45001, National Carrier License",
  icon: "building"
}, {
  id: 6,
  name: "Global Warehousing Inc",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/global_warehousing_logo.png",
  type: "Storage & Fulfillment",
  description: "Strategically located bonded warehouses in key European and Indian trade zones.",
  certificates: "TAPA FSR Class A, ISO 9001",
  icon: "package"
}];
const getIcon = (type: string) => {
  switch (type) {
    case "plane":
      return <Plane className="size-5 text-muted-foreground" />;
    case "anchor":
      return <Anchor className="size-5 text-muted-foreground" />;
    case "globe":
      return <Globe className="size-5 text-muted-foreground" />;
    case "truck":
      return <Truck className="size-5 text-muted-foreground" />;
    case "building":
      return <Building2 className="size-5 text-muted-foreground" />;
    case "package":
      return <PackageCheck className="size-5 text-muted-foreground" />;
    default:
      return <Globe className="size-5 text-muted-foreground" />;
  }
};
export default function TheLogisticsNetwork() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-logistics-network" className="w-full py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`flex flex-col items-center text-center space-y-4 mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="outline" className="px-3 py-1 text-sm border-primary/20 bg-primary/5 text-primary">
            Verified Partners
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight font-serif text-primary">
            The Logistics Network
          </h2>
          <p className="max-w-[800px] text-lg text-muted-foreground md:text-xl">
            Connect with our curated ecosystem of certified logistics providers, ensuring seamless cross-border trade between Europe and India.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-1000 ease-out delay-200 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {partners.map((partner, index) => <div data-index={index} key={partner.id} className="h-full">
              <Card className="h-full flex flex-col hover:shadow-lg transition-shadow duration-300 border-border/50 bg-card/50 backdrop-blur-sm">
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start mb-4">
                    <div className="p-2 bg-muted rounded-lg">
                      {getIcon(partner.icon)}
                    </div>
                    <Badge variant="outline" className="bg-accent text-accent-foreground border-accent-foreground/20 gap-1.5 py-1 px-2.5 shadow-xs">
                      <ShieldCheck className="size-3.5" />
                      Verified
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-semibold text-primary">
                    {partner.name}
                  </CardTitle>
                  <CardDescription className="text-sm font-medium text-secondary">
                    {partner.type}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1">
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {partner.description}
                  </p>
                </CardContent>
                <CardFooter className="pt-4 border-t border-border/50 mt-auto">
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <Button variant="ghost" className="w-full justify-between text-muted-foreground hover:text-primary hover:bg-primary/5 group">
                        <span className="text-sm font-medium">View Certifications</span>
                        <Info className="size-4 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </Button>
                    </HoverCardTrigger>
                    <HoverCardContent align="end" className="w-80 bg-popover/95 backdrop-blur-md border-border/50 shadow-xl">
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-popover-foreground flex items-center gap-2">
                          <ShieldCheck className="size-4 text-secondary" />
                          Compliance & Standards
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {partner.certificates}
                        </p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}